import { getFiltersPersistKey } from '@/helpers/persistScope';
import type { IStorageFiltersOptions } from '@/types';

export function usePersistentFilters<T extends object>(options: IStorageFiltersOptions<T>) {
    const { filters, pageId, enabled, transform } = options;

    const enabledRef = enabled || ref(true);

    const keysToPersist =
        options.keysToPersist ||
        (Object.keys(filters).filter((key) => (filters as Record<string, { allowSaveState?: boolean }>)[key]?.allowSaveState) as (keyof T)[]);

    const storageKey = computed(() => getFiltersPersistKey(pageId));

    const isValidFilterStructure = (parsedItem: any, defaultItem: any): boolean => {
        if (typeof parsedItem !== 'object' || parsedItem === null) {
            return false;
        }

        if (!('value' in parsedItem) || !('title' in parsedItem)) {
            return false;
        }

        if (parsedItem.title !== defaultItem.title) {
            return false;
        }

        return true;
    };

    const transformDates = (value: any, originalValue: any): any => {
        if (Array.isArray(value) && Array.isArray(originalValue)) {
            return value.map((item, index) => transformDates(item, originalValue[index]));
        }

        if (originalValue instanceof Date && typeof value === 'string') {
            return new Date(value);
        }

        if (value && typeof value === 'object' && !Array.isArray(value) && originalValue && typeof originalValue === 'object') {
            const result: any = {};
            for (const key in value) {
                result[key] = transformDates(value[key], originalValue[key]);
            }
            return result;
        }

        return value;
    };

    const loadFromStorage = () => {
        if (!enabledRef.value) return;

        const saved = localStorage.getItem(storageKey.value);
        if (!saved) return;

        try {
            let parsed = JSON.parse(saved);

            if (transform?.load) {
                parsed = transform.load(parsed);
            }

            let hasInvalidData = false;

            for (const key of keysToPersist) {
                if (key in parsed) {
                    if (!isValidFilterStructure(parsed[key], filters[key])) {
                        console.warn(`Invalid filter structure for key "${String(key)}", using default value`);
                        hasInvalidData = true;
                        continue;
                    }
                    filters[key] = transformDates(parsed[key], filters[key]);
                }
            }

            if (hasInvalidData) {
                saveToStorage();
            }
        } catch (e) {
            console.warn(`Failed to parse stored filters from "${storageKey.value}"`, e);
            clearStorage();
            saveToStorage();
        }
    };

    const saveToStorage = () => {
        if (!enabledRef.value) return;

        let dataToSave: Partial<T> = {};
        for (const key of keysToPersist) {
            dataToSave[key] = filters[key];
        }

        if (transform?.save) {
            dataToSave = transform.save(dataToSave);
        }

        localStorage.setItem(storageKey.value, JSON.stringify(dataToSave));
    };

    const clearStorage = () => {
        localStorage.removeItem(storageKey.value);
    };

    const refs = toRefs(filters);

    watchEffect((onCleanup) => {
        const watchers: (() => void)[] = [];

        for (const key of keysToPersist) {
            const stopWatch = watch(
                refs[key],
                () => {
                    saveToStorage();
                },
                { deep: true }
            );
            watchers.push(stopWatch);
        }

        onCleanup(() => {
            watchers.forEach((stop) => stop());
        });
    });

    watch(
        enabledRef,
        (isEnabled) => {
            if (isEnabled) {
                loadFromStorage();
                saveToStorage();
            } else {
                clearStorage();
            }
        },
        { flush: 'sync' }
    );

    loadFromStorage();

    return {
        loadFromStorage,
        saveToStorage,
        clearStorage,
        storageKey,
    };
}
